import { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { signInWithEmailAndPassword, createUserWithEmailAndPassword } from "firebase/auth";
import { auth } from "../firebase";
import { EncryptionContext } from "./EncryptionContext";

function LoginPage() { // Removed setDecryptionError prop
  const [isSignupMode, setIsSignupMode] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passphraseInput, setPassphraseInput] = useState("");
  const [error, setError] = useState("");
  const { setPassphrase } = useContext(EncryptionContext);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (isSignupMode && password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    try {
      let userCredential;
      if (isSignupMode) {
        userCredential = await createUserWithEmailAndPassword(auth, email, password);
      } else {
        userCredential = await signInWithEmailAndPassword(auth, email, password);
      }
      console.log("Login successful, user:", userCredential.user.uid, "hasOptionalPassphrase:", Boolean(passphraseInput));
      localStorage.setItem("userUid", userCredential.user.uid);
      if (passphraseInput) {
        localStorage.setItem("passphrase", passphraseInput);
      } else {
        localStorage.removeItem("passphrase");
      }
      setPassphrase(passphraseInput);
      // Removed setDecryptionError("") call
      navigate("/cycles");
    } catch (err) {
      console.error("Login error:", err);
      if (err.code === "auth/wrong-password" || err.code === "auth/user-not-found") {
        setError("Invalid email or password. Please try again or reset your password.");
      } else if (err.code === "auth/email-already-in-use") {
        setError("Email already in use. Please log in or use a different email.");
      } else {
        setError(
          isSignupMode
            ? "Failed to create account. Please check your credentials or try a different email."
            : "Failed to log in. Please check your credentials."
        );
      }
    }
  };

  const handlePassphraseReset = () => {
    localStorage.removeItem("encryptedPassphrase");
    localStorage.removeItem("userUid");
    localStorage.removeItem("passphrase");
    setPassphrase("");
    setPassphraseInput("");
    setError("Optional encryption passphrase reset. You can log in without it, or enter the passphrase used for older encrypted data.");
  };

  return (
    <div className="bg-background-light dark:bg-background-dark min-h-screen flex items-center justify-center px-4 sm:px-6">
      <div className="card p-6 w-full max-w-md">
        <h1 className="text-3xl font-bold text-text-light dark:text-text-dark mb-2 text-center">
          66 Days Journal
        </h1>
        <h2 className="text-lg text-text-light dark:text-text-dark mb-4 text-center">
          Transform Your Life: Log in or sign up to start your journaling journey.
        </h2>
        {error && (
          <p className="text-error mb-4 text-center">
            {error}
            {error.includes("Failed to log in") && (
              <>
                <br />
                <button
                  onClick={handlePassphraseReset}
                  className="text-secondary hover:underline"
                  aria-label="Reset optional encryption passphrase and try again"
                >
                  Reset optional passphrase
                </button>
              </>
            )}
          </p>
        )}
        <div className="flex justify-center gap-4 mb-4">
          <button
            onClick={() => setIsSignupMode(false)}
            className={`tab ${!isSignupMode ? "tab-active" : "tab-inactive"}`}
            aria-label="Switch to login mode"
          >
            Login
          </button>
          <button
            onClick={() => setIsSignupMode(true)}
            className={`tab ${isSignupMode ? "tab-active" : "tab-inactive"}`}
            aria-label="Switch to sign up mode"
          >
            Sign Up
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-text-light dark:text-text-dark font-medium mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input w-full"
              required
              aria-label="Email address"
            />
          </div>
          <div>
            <label className="block text-text-light dark:text-text-dark font-medium mb-1">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input w-full"
              required
              aria-label="Password"
            />
          </div>
          {isSignupMode && (
            <div>
              <label className="block text-text-light dark:text-text-dark font-medium mb-1">
                Confirm Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="input w-full"
                required
                aria-label="Confirm password"
              />
            </div>
          )}
          <div>
            <label className="block text-text-light dark:text-text-dark font-medium mb-1">Optional encryption passphrase</label>
            <input
              type="password"
              value={passphraseInput}
              onChange={(e) => setPassphraseInput(e.target.value)}
              className="input w-full"
              aria-label="Optional encryption passphrase"
            />
          </div>
          <div className="flex gap-2">
            <button
              type="submit"
              className="btn-primary w-full"
              aria-label={isSignupMode ? "Create account" : "Log in"}
            >
              {isSignupMode ? "Create Account" : "Log In"}
            </button>
          </div>
        </form>
        <div className="mt-4 p-4 bg-neutral/10 dark:bg-neutral/20 rounded text-text-light dark:text-text-dark">
          <p className="text-sm">
            <strong>About encryption</strong><br />
            You can leave the encryption passphrase blank. When it is blank, your logged-in account is used to keep entries writable without an extra secret stopping you. If you previously saved data with a passphrase, enter that same passphrase to read that older data.
          </p>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;